import * as React from 'react'

import { Button, Notice } from '@buffer-mono/popcorn'

import { useHasOrganizationAction } from '~publish/contexts/AccountContext'
import { useRegisteredBanners } from '~publish/components/RegisteredBanner'
import { useRefreshChannelConnection } from '~publish/hooks/useRefreshChannelConnection'

import { useSelectedChannel } from './ChannelContext'

import styles from './DisconnectedChannelNotice.module.css'

import type { JSX } from 'react'

export const DisconnectedChannelNotice = (): JSX.Element | null => {
  const selectedChannel = useSelectedChannel()
  const refreshChannelConnection = useRefreshChannelConnection()
  const canManageChannels = useHasOrganizationAction('manageChannels')

  const canRender = selectedChannel.isDisconnected

  // Suppress banners if this notice is visible
  const { suppressBanners, unsuppressBanners } = useRegisteredBanners()
  React.useLayoutEffect(() => {
    if (canRender) {
      suppressBanners()
    }
    return (): void => unsuppressBanners()
  }, [canRender, suppressBanners, unsuppressBanners])

  if (!canRender) {
    return null
  }

  const handleReconnect = (): void => {
    refreshChannelConnection({
      channel: {
        id: selectedChannel.id,
        service: selectedChannel.service,
        name: selectedChannel.name,
        apiVersion: selectedChannel.apiVersion,
      },
      cta: 'publish-channelPage-notice-refreshConnection-1',
    })
  }

  return (
    <Notice variant="error" className={styles.notice}>
      <Notice.Heading>We&apos;ve lost access to this channel</Notice.Heading>
      <Notice.Text>
        {canManageChannels
          ? 'Posts for this channel will not get published until you refresh the connection.'
          : "Posts for this channel will not get published until the connection is refreshed. You don't have permission to refresh the connection. Please contact the administrator of your organization."}
      </Notice.Text>
      {canManageChannels && (
        <Notice.Actions>
          <Button variant="critical" onClick={handleReconnect}>
            Refresh Connection
          </Button>
        </Notice.Actions>
      )}
    </Notice>
  )
}
